import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { deburr } from 'lodash-es';
import { Divider } from 'primeng/divider';
import { Popover, PopoverModule } from 'primeng/popover';

import { HideOnWindowScrollDirective } from '../../directives/hide-on-window-scroll/hide-on-window-scroll.directive';
import { MInputSearchComponent } from '../m-input-search/m-input-search.component';
import { MNavTriggerComponent } from '../m-nav-trigger';
import {
  BrandPickerOption,
  BrandPickerSelectEvent
} from './m-brand-picker.types';

/** Stable identity for a brand option — used for tracking and dedupe. */
function brandKey(brand: BrandPickerOption): string {
  return `${brand.clientId}-${brand.brandId}`;
}

@Component({
  selector: 'm-brand-picker',
  templateUrl: './m-brand-picker.component.html',
  styleUrls: ['./m-brand-picker.component.scss'],
  imports: [
    NgTemplateOutlet,
    FormsModule,
    PopoverModule,
    HideOnWindowScrollDirective,
    MNavTriggerComponent,
    MInputSearchComponent,
    Divider
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MBrandPickerComponent {
  readonly popover = viewChild<Popover>('popover');
  readonly searchInput = viewChild(MInputSearchComponent);
  readonly brandList = viewChild<ElementRef<HTMLElement>>('brandList');

  readonly label = input('');
  readonly ariaLabel = input<string>();
  readonly disabled = input(false);
  readonly brands = input<BrandPickerOption[]>([]);
  readonly recentBrands = input<BrandPickerOption[]>([]);

  readonly brandSelected = output<BrandPickerSelectEvent>();
  readonly panelShow = output<void>();
  readonly panelHide = output<void>();

  readonly isOpen = signal(false);
  readonly searchQuery = signal('');
  readonly activeBrandIndex = signal(-1);

  readonly isInteractive = computed(() => this.brands().length >= 2);

  /** Grouped recents/divider layout only when not searching and recents exist. */
  readonly showRecents = computed(
    () => !this.searchQuery().trim() && this.recentBrands().length > 0
  );

  /** Full list minus the recents (no duplicates in the lower section). */
  readonly rest = computed(() => {
    const recentKeys = new Set(this.recentBrands().map(brandKey));
    return this.brands().filter((brand) => !recentKeys.has(brandKey(brand)));
  });

  /** Brand names deburred once per list change, index-aligned with brands(). */
  private readonly normalizedNames = computed(() =>
    this.brands().map((brand) => deburr(brand.name.toLowerCase()))
  );

  readonly filteredBrands = computed(() => {
    const query = deburr(this.searchQuery().trim().toLowerCase());
    if (!query) {
      return this.brands();
    }
    const names = this.normalizedNames();
    return this.brands().filter(
      (brand, i) =>
        (names[i] ?? '').includes(query) ||
        String(brand.clientId).includes(query) ||
        String(brand.brandId).includes(query)
    );
  });

  readonly displayedBrands = computed(() =>
    this.showRecents()
      ? [...this.recentBrands(), ...this.rest()]
      : this.filteredBrands()
  );

  togglePanel(event: Event): void {
    if (this.disabled()) {
      return;
    }
    this.popover()?.toggle(event);
  }

  onShow(): void {
    this.isOpen.set(true);
    this.activeBrandIndex.set(-1);
    this.panelShow.emit();
    setTimeout(() => this.searchInput()?.focus(), 0);
  }

  onHide(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.activeBrandIndex.set(-1);
    this.panelHide.emit();
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.activeBrandIndex.set(-1);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const brands = this.displayedBrands();
    if (!brands.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActiveBrandIndex(
        Math.min(this.activeBrandIndex() + 1, brands.length - 1)
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActiveBrandIndex(
        this.activeBrandIndex() < 0
          ? brands.length - 1
          : Math.max(this.activeBrandIndex() - 1, 0)
      );
      return;
    }

    if (event.key === 'Enter' && this.activeBrandIndex() >= 0) {
      event.preventDefault();
      this.onBrandClick(brands[this.activeBrandIndex()]!, event);
    }
  }

  onBrandClick(brand: BrandPickerOption, event: Event): void {
    // When the item is an anchor and the user requested a new tab/window
    // (modifier or middle click), let the browser follow the href natively and
    // leave the current tab's selection untouched.
    if (brand.url && this.isNewTabClick(event)) {
      return;
    }
    // For anchors, suppress the default navigation so plain left-click / Enter
    // keeps the existing single-page brand switch behavior.
    event.preventDefault();
    this.brandSelected.emit({ brand, event });
    this.popover()?.hide();
  }

  private isNewTabClick(event: Event): boolean {
    if (!(event instanceof MouseEvent)) {
      return false;
    }
    return (
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey ||
      event.button === 1
    );
  }

  private setActiveBrandIndex(index: number): void {
    this.activeBrandIndex.set(index);
    const brandItem =
      this.brandList()?.nativeElement.querySelectorAll('.brand-picker-item')[
        index
      ];
    brandItem?.scrollIntoView({ block: 'nearest' });
  }
}
